"use client"

import { useEffect } from "react"
import Script from "next/script"
import { 
  Clock, 
  Users, 
  CheckCircle2, 
  Calendar,
  Star
} from 'lucide-react'

// Add testimonials data
const testimonials = [
  {
    name: "Darren Wilson",
    role: "Sales Director",
    company: "TechSecure Solutions",
    quote: "The demo was eye-opening. We've since implemented the AI sales automation and seen a 40% increase in qualified leads.",
    rating: 5
  },
  {
    name: "Victor Nguyen",
    role: "VP of Sales",
    company: "CloudServe MSP",
    quote: "What impressed me most was how they tailored the demo to our specific MSP challenges.",
    rating: 5
  },
  {
    name: "Emma Thompson",
    role: "Sales Operations Manager",
    company: "Cyber Shield Inc",
    quote: "The personalized demo showed us exactly how to automate our complex security solution sales. Implementation was smooth and the results are outstanding.",
    rating: 5
  }
]

export default function BookDemoClient() {
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.hasOwnProperty('frameHeight')) {
        const iframe = document.getElementById('bookingFrame')
        if (iframe) {
          iframe.style.height = `${event.data.frameHeight}px`
        }
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-1">
        <div className="container max-w-6xl px-4 py-16 md:py-24">
          <div className="mb-12 text-center">
            <h1 className="text-3xl font-bold tracking-tight mb-4">
              Book Your Personalized Demo
            </h1>
            <p className="text-gray-600">
            See how our AI-powered sales automation can transform your business
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Booking Calendar */}
            <div className="min-h-[600px] w-full bg-white rounded-lg shadow-sm overflow-hidden">
              <iframe 
                src="https://api.robotsalesrep.ai/widget/booking/7iSSynzhdI3On1JqdQn6"
                id="bookingFrame"
                className="w-full min-h-[600px] border-0"
                scrolling="no"
                title="Book a Demo Calendar"
              />
              <Script 
                src="https://api.robotsalesrep.ai/js/form_embed.js"
                strategy="afterInteractive"
              />
            </div>

            {/* What to Expect Section */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold mb-6">What to Expect</h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">30-Minute Personalized Discovery meeting</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        Let's discuss your needs with our AI sales automation experts
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Custom-Tailored Demo</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        We'll show you how our platform can solve your specific sales challenges.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1">Actionable Insights</h3>
                      <p className="text-gray-600 dark:text-gray-300">
                        Leave with a clear understanding of how AI can boost your sales pipeline.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  <h3 className="font-semibold text-lg">Next Steps</h3>
                </div>
                <ol className="list-decimal list-inside space-y-3 text-gray-600 dark:text-gray-300">
                  <li>Select a date and time that works for you.</li>
                  <li>Fill out a brief form to help us prepare.</li>
                  <li>Receive a confirmation email with your meeting link.</li>
                </ol>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/30 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800/50">
                <h3 className="font-semibold mb-2">💡 Pro Tip</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  Come prepared with questions about your specific sales challenges. 
                  This helps us tailor the demo to your needs and show you the most relevant features.
                </p>
              </div>
            </div>
          </div>

          {/* Testimonials Section */}
          <div className="mt-24">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold">What Our Clients Say</h2>
              <p className="text-gray-600 dark:text-gray-300 mt-2">
                Hear from sales leaders who've transformed their processes
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={index}
                  className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  {/* Rating */}
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star 
                        key={i}
                        className="h-5 w-5 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>

                  {/* Quote */}
                  <blockquote className="text-gray-700 dark:text-gray-300 mb-6">
                    {testimonial.quote}

                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center">
                    <div className="relative w-12 h-12 rounded-full overflow-hidden mr-4">
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {testimonial.name}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {testimonial.role}
                      </div>
                      <div className="text-sm text-blue-600 dark:text-blue-400">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Proof Stats */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  500+
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Demos Delivered
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  95%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Satisfaction Rate
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  40%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Avg. Efficiency Gain
                </div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  2X
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Sales Pipeline Growth
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
